import type { ModeId } from '@vm/shared';
import type { MapBuilder } from './builder';

export interface SpawnPoint {
  /** -1 = any team (FFA). */
  team: number;
  x: number;
  y: number;
  z: number;
  yaw: number;
}

export interface CapturePoint {
  id: string;
  name: string;
  x: number;
  y: number;
  z: number;
  r: number;
}

export interface FlagBase {
  team: number;
  x: number;
  y: number;
  z: number;
}

/** Demolition core: a crystal region in voxel coordinates (half-open). */
export interface CoreRegion {
  team: number;
  x0: number;
  y0: number;
  z0: number;
  x1: number;
  y1: number;
  z1: number;
}

export interface PickupSpot {
  kind: 'health' | 'armor' | 'ammo' | 'weapon' | 'gadget';
  x: number;
  y: number;
  z: number;
}

/** Positions are meters unless noted. */
export interface MapMeta {
  spawns: SpawnPoint[];
  points: CapturePoint[];
  flags: FlagBase[];
  cores: CoreRegion[];
  pickups: PickupSpot[];
  /** Zombie/enemy spawn locations (PvE). */
  enemySpawns: { x: number; y: number; z: number }[];
  /** Walking surface height of open ground, meters. */
  groundY: number;
  /** Center of play for BR zones and camera fly-ins. */
  center: { x: number; z: number };
}

export interface MapTheme {
  skyTop: number;
  skyHorizon: number;
  fog: number;
  fogNear: number;
  fogFar: number;
  /** Direction toward the sun (normalized by the renderer). */
  sun: [number, number, number];
  sunColor: number;
  sunIntensity: number;
  ambient: number;
  ambientIntensity: number;
  /** Color of the endless ground plane outside the map. */
  outside: number;
  gravity?: number;
  music?: string;
  night?: boolean;
}

export interface MapDef {
  id: string;
  name: string;
  description: string;
  /** World size in voxels (multiples of 32). */
  size: [number, number, number];
  modes: ModeId[];
  players: [number, number];
  theme: MapTheme;
  /** Default generation seed. Maps look the same every match unless a mode overrides it. */
  seed: number;
  /** Bump when the generator output changes (part of cache keys). */
  version: number;
  build(b: MapBuilder): MapMeta;
}

export function emptyMeta(groundY: number, cx: number, cz: number): MapMeta {
  return { spawns: [], points: [], flags: [], cores: [], pickups: [], enemySpawns: [], groundY, center: { x: cx, z: cz } };
}
